import { InitialState } from 'const/states'
// import { TestState as InitialState } from 'const/states'
import { FormEventHandler, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type {
  ComponentInterface,
  FormState,
  HandleOnChange,
  SFDDLProps,
  ErrorParsed,
  FormErrors
} from 'typings'
import utagUtils from 'utils/utag'
import validate from 'helpers/fieldValidations'
import { getRequestTypeEntity } from 'helpers'
import errorMessages from 'const/errorMessages'
import {
  ButtonContainer,
  PrimaryButton,
  Form,
  GridHeading
} from 'ui/molecules/RequestPage.styles'
import MailingAddress from 'ui/molecules/MailingAddress'
import PersonalInformation from 'ui/molecules/PersonalInformation'
import ContactInformation from 'ui/molecules/ContactInformation'
import RelationShip from 'ui/molecules/Relationship'
import RequestType from 'ui/molecules/RequestType'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'const/config'
import caseCreation from 'services/caseCreation'
import submitRequest from 'services/submitRequest'
import ErrorMessage from 'ui/atoms/Error'
import OTPModal from 'ui/atoms/OTPModal'
import useCustomTranslation from 'hooks/useCustomTranslation'
import sessionKeys from 'const/sessionKeys'
import FullPageLoad from 'ui/atoms/FullPageLoad'
import {
  Headline,
  StyledCaptcha,
  StyledCheckbox
} from './InfoRequestPage.styles'

// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace window {
  // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
  const _SFDDL: SFDDLProps
}

const setRequestTypeAnalytics = (value: string, checked: boolean) => {
  // eslint-disable-next-line no-undef, @typescript-eslint/naming-convention, no-underscore-dangle
  const { RequestType: requestType } = window._SFDDL.requestcheckboxInfo
  let values = requestType ? requestType.split('|') : []
  if (checked) {
    values.push(value)
  } else {
    values = values.filter(item => item !== value)
  }
  // eslint-disable-next-line no-undef, @typescript-eslint/naming-convention, no-underscore-dangle
  window._SFDDL.requestcheckboxInfo.RequestType = values.join('|')
}

const InfoRequestPage = (): JSX.Element => {
  const navigate = useNavigate()
  const t = useCustomTranslation()
  const [form, setForm] = useState<FormState>(InitialState)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [otpToken, setOTPToken] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const expiredMessage: ErrorParsed = {
    statusCode: 0,
    description: t('form.errors.expired'),
    businessErrorCode: '',
    errors: []
  }

  useEffect(() => {
    sessionStorage.removeItem(sessionKeys.recaptchaToken)
    // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
    const sfddl = window._SFDDL || { pageInfo: {}, requestcheckboxInfo: {} }
    if (sfddl && sfddl.pageInfo) {
      sfddl.pageInfo.PageSubFunction = ''
      sfddl.pageInfo.PageName = 'start'
    }
    if (sfddl.requestcheckboxInfo) {
      sfddl.requestcheckboxInfo = {
        RequestType: '',
        RectificationType: ''
      }
    }
    // update view to track events
    utagUtils.view()
  }, [])

  /**
   * Set value and validate field value
   * @param target prop name
   * @param value value
   * @param additionalState extra props to update state
   */
  const handleOnChange: HandleOnChange = (
    target,
    value,
    additionalState = {}
  ) => {
    const validation = validate(target, value, form)
    setForm(prevState => {
      return {
        ...prevState,
        ...additionalState,
        [target]: value,
        errors: {
          ...additionalState?.errors,
          ...prevState.errors,
          [target]: validation
        }
      }
    })
  }

  const handleOnBlur = (target: string) => {
    setForm(prevState => {
      return {
        ...prevState,
        errors: { ...form.errors, [target]: errorMessages.required }
      }
    })
  }

  const handleState = (formValues: FormState) => {
    setForm(prevState => {
      return {
        ...prevState,
        ...formValues
      }
    })
  }

  const displayErrorMessage = (error: ErrorParsed) => {
    setForm({
      ...form,
      isSubmitted: false,
      submissionError: (error as ErrorParsed)?.description
    })
  }

  const createCaseRequest = async (jwt: string) => {
    const captchaToken =
      sessionStorage.getItem(sessionKeys.recaptchaToken) || ''
    const caseCreationResponse = await caseCreation(captchaToken, jwt)
    const trackingId = caseCreationResponse.data.transactionId
    const status = caseCreationResponse.data.jwtTokenValid
    navigate('/complete', {
      state: { trackingId, status }
    })
  }

  const submitForm = async (skipOTPVerification: boolean, jwt = '') => {
    const captchaToken =
      sessionStorage.getItem(sessionKeys.recaptchaToken) || ''
    setForm({
      ...form,
      isSubmitted: true
    })
    if (!captchaToken) {
      displayErrorMessage(expiredMessage)
      return
    }
    try {
      if (!skipOTPVerification && !jwt) {
        setOpenModal(true)
      } else {
        setIsLoading(true)
        await submitRequest(form, captchaToken, jwt)
        await createCaseRequest(jwt)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      displayErrorMessage(e as ErrorParsed)
    } finally {
      setIsLoading(false)
    }
  }

  const setOTPStatus = async (status?: boolean, jwt?: string) => {
    const token = jwt ?? ''
    setOTPToken(token)
    submitForm(!jwt && !status, token)
    setOpenModal(false)
  }

  // Mirrors the payload builder so validation cannot pass while the payload is empty.
  const validateRequestType = () => getRequestTypeEntity(form).length > 0

  const validateAllFields = (): Partial<FormErrors> => {
    let errors: Partial<FormErrors> = {
      recaptchaToken: validate('recaptchaToken', '', form)
    }
    Object.keys(form).forEach((target: string) => {
      const error = validate(target, form[target as keyof FormState], form)
      errors = { ...errors, [target]: error }
    })
    return errors
  }

  const handleSubmit = async (event: Event) => {
    event.preventDefault()
    setForm({
      ...form,
      submissionError: ''
    })
    const isRequestTypeValid = validateRequestType()
    const errorsList = validateAllFields()
    const isFormValid = Object.values(errorsList).every(x => !x)
    if (isRequestTypeValid && isFormValid) {
      const skipOTPVerification = form.hardOfHearing
      submitForm(skipOTPVerification, otpToken)
    } else {
      setForm({
        ...form,
        errors: {
          ...form.errors,
          ...errorsList
        },
        requestTypeMissing: !isRequestTypeValid,
        submissionError: t('form.errors.submission')
      })
    }
  }

  const setReCaptcha = (captcha: string | null) => {
    if (captcha) {
      sessionStorage.setItem(sessionKeys.recaptchaToken, captcha)
      if (otpToken) {
        submitForm(true, otpToken)
      }
    } else {
      sessionStorage.removeItem(sessionKeys.recaptchaToken)
    }
  }

  const handlers: ComponentInterface = {
    formValues: form,
    handleOnChange,
    handleOnBlur,
    handleState
  }

  return (
    <Form
      onSubmit={handleSubmit as unknown as FormEventHandler<HTMLFormElement>}
      data-test="form-submit"
    >
      {isLoading && <FullPageLoad />}
      <Headline>{t('form.headline')}</Headline>
      <div>
        <span>{t('form.required')}</span>
      </div>
      <MailingAddress {...handlers} />
      {form.fromStateWhitelist.value === 'Yes' && (
        <>
          <PersonalInformation {...handlers} />
          <ContactInformation {...handlers} />
          <RelationShip {...handlers} />
          <RequestType {...handlers} />
          <StyledCheckbox
            id="language"
            isChecked={form.language === 'Spanish'}
            onChange={() => {
              handleOnChange(
                'language',
                form.language === 'English' ? 'Spanish' : 'English'
              )
              setRequestTypeAnalytics('Spanish', form.language === 'English')
            }}
            data-testid="language"
            data-reason="correspondenceforthisrequestinspanish"
            data-type="Checkbox"
            className="ensightencustomevent"
          >
            {t('form.request.correspondence')}
          </StyledCheckbox>
          {form.errors.recaptchaToken && (
            <GridHeading style={{ marginBottom: 12, marginTop: 12 }}>
              <StyledCaptcha>{form.errors.recaptchaToken}</StyledCaptcha>
            </GridHeading>
          )}
          <ReCAPTCHA
            sitekey={config.RECAPTCHA_KEY}
            hl={t('language').toLowerCase()}
            onChange={(token: string | null) => setReCaptcha(token)}
          />
        </>
      )}
      <ButtonContainer>
        <ErrorMessage error={form.submissionError} />
        <PrimaryButton
          type="submit"
          disabled={form.isSubmitted || form.fromStateWhitelist.value !== 'Yes'}
          data-test="submit-button"
          className="ensightencustomevent"
          data-type="button"
          data-reason="submitrequest"
          data-object="cprasubmit"
        >
          {t('form.submit')}
        </PrimaryButton>
      </ButtonContainer>
      <OTPModal
        data-test="otp-popup"
        OTPCallback={setOTPStatus}
        formValues={form}
        open={openModal}
      />
    </Form>
  )
}

export default InfoRequestPage
